import "./ModalPromotion.scss";

import { Modal, ProgressBar } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppContainer from "../../../commons/containers/AppContainer";
import HallAppToast from "../../../commons/HallAppToast";
import PromotionAPI from "../PromotionAPI";
import PromotionStep1 from "./steps/PromotionStep1";
import PromotionStep2 from "./steps/PromotionStep2";
import PromotionStep3 from "./steps/PromotionStep3";
import PromotionStep4 from "./steps/PromotionStep4";

const promotionAPI = new PromotionAPI();

function StepDots({ step, total }) {
  return (
    <div className="d-flex align-items-center gap-2 w-100">
      {Array.from({ length: total }).map((_, idx) => {
        const i = idx + 1;
        const active = i <= step;

        return (
          <div key={i} className="d-flex align-items-center flex-grow-1">
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: active ? "#2f2a7a" : "#e7e7f4",
                border: active ? "1px solid #2f2a7a" : "1px solid #d8d8ee",
              }}
              aria-label={`Passo ${i}`}
            />
            {i !== total && (
              <div
                style={{
                  height: 2,
                  flex: 1,
                  marginLeft: 8,
                  marginRight: 8,
                  background: i < step ? "#2f2a7a" : "#e7e7f4",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// helpers (bem leves, sem mudar suas regras)
const normalizePromotionDTOToWizardData = (dto) => {
  if (!dto) return {};

  const startHour = dto.startHour || "";
  const endHour = dto.endHour || "";
  const noEndDate = Boolean(dto.noEndDate) || !dto.endDate;

  return {
    ...dto,
    startHour,
    endHour,
    noEndDate,
    // employeeServices já vem no formato certo: [{ serviceId, employeeId, percentage }]
    employeeServices: Array.isArray(dto.employeeServices) ? dto.employeeServices : [],
    validWeeks: Array.isArray(dto.validWeeks) ? dto.validWeeks : [],
  };
};

function ModalPromotion({ show, closeShow, onSaved, promotionId }) {
  const TOTAL_STEPS = 4;

  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  const progress = useMemo(() => Math.round((step / TOTAL_STEPS) * 100), [step]);

  const next = (value) => {
    setData((prev) => ({ ...prev, ...value }));
    setStep((prev) => prev + 1);
  };

  const back = () => setStep((prev) => prev - 1);

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return "Básico";
      case 2:
        return "Serviços e preço";
      case 3:
        return "Exibição e regras";
      case 4:
        return "Revisão e confirmação";
      default:
        return "Promoções";
    }
  }, [step]);

  // ✅ quando abrir para editar (promotionId), buscar no backend
  useEffect(() => {
    const loadEdit = async () => {
      if (!show) return;

      // criar novo
      if (!promotionId) {
        setStep(1);
        setData({});
        return;
      }

      try {
        setIsLoadingEdit(true);
        setStep(1);

        const resp = await promotionAPI.getPromotionById(promotionId);
        const dto = resp?.data;

        setData(normalizePromotionDTOToWizardData(dto));
      } catch (e) {
        HallAppToast.genericError();
        // se falhar, fecha (pra não ficar wizard vazio)
        closeShow?.();
      } finally {
        setIsLoadingEdit(false);
      }
    };

    loadEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, promotionId]);

  const resetAndClose = () => {
    setStep(1);
    setData({});
    closeShow?.();
  };

  return (
    <Modal
      show={show}
      fullscreen="md-down"
      centered
      size="lg"
      onHide={resetAndClose}
      contentClassName="hallapp-modal modal-promotion"
    >
      <Modal.Header closeButton className="border-bottom">
        <div className="w-100">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: "20px" }}>
                {promotionId ? "Editar promoção" : "Nova promoção"}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {isLoadingEdit ? "Carregando dados..." : `${stepTitle} • Passo ${step} de ${TOTAL_STEPS}`}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <StepDots step={step} total={TOTAL_STEPS} />
            <div className="mt-2">
              <ProgressBar now={progress} style={{ height: 6, borderRadius: 999 }} />
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <AppContainer>
          {/* enquanto carrega edição, não renderiza steps */}
          {isLoadingEdit ? (
            <div style={{ padding: 20 }}>Carregando...</div>
          ) : (
            <>
              {step === 1 && <PromotionStep1 nextStep={next} initialData={data} />}

              {step === 2 && (
                <PromotionStep2 nextStep={next} toBack={back} initialData={data} />
              )}

              {step === 3 && (
                <PromotionStep3 nextStep={next} toBack={back} initialData={data} />
              )}

              {step === 4 && (
                <PromotionStep4
                  data={data}
                  toBack={back}
                  closeModal={resetAndClose}
                  onSaved={onSaved}
                  promotionId={promotionId} // opcional: pra Step4 saber se é update
                />
              )}
            </>
          )}
        </AppContainer>
      </Modal.Body>
    </Modal>
  );
}

export default ModalPromotion;
